import { ToolCall } from "@cohesive/api-client";

// How "settled" a tool call's representation is, used to pick a winner when two
// entries share an `index` (a streaming placeholder vs. its terminal/persisted
// version). Higher wins: real results beat a confirmation state beat any
// non-"processing" status beat a bare placeholder.
function toolCallCompleteness(call: ToolCall): number {
  let score = 0;
  if (call.result?.data || call.function.result?.data) score += 4;
  if (call.user_confirmation_status) score += 2;
  if (call.status && call.status !== "processing") score += 1;
  return score;
}

/**
 * Keep one tool call per `index`, choosing the most complete representation.
 * Preserves index order and is a no-op for the common no-duplicate case.
 *
 * Lives in its own leaf module (re-exported from `ToolCall.tsx`, so import
 * sites are unchanged) because non-rendering consumers — the COT step count and
 * the chat's canvas-add summary — need to dedupe identically to what actually
 * renders without pulling in the renderer's React/JsonForms dependency tree.
 */
export function dedupeToolCallsByIndex(
  calls: ToolCall[] | undefined,
): ToolCall[] | undefined {
  if (!calls || calls.length < 2) return calls;
  const byIndex = new Map<number, ToolCall>();
  for (const call of calls) {
    const existing = byIndex.get(call.index);
    if (
      !existing ||
      toolCallCompleteness(call) >= toolCallCompleteness(existing)
    ) {
      byIndex.set(call.index, call);
    }
  }
  if (byIndex.size === calls.length) return calls; // nothing collapsed
  return [...byIndex.values()].sort((a, b) => a.index - b.index);
}
